import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import { Result, Skeleton } from 'antd'
import { ResultStatusType } from 'antd/lib/result'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { getEmployeeFormData } from './selectors'
import { EmployeeFormData } from './types/types'
import { EmployeeFormHeader } from './ui/forms/form-header'
import { EmployeeCardContent } from './ui/employee-card-content'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { setTimelineClear } from '../../../store/timeline/actions'
import { getTimeLineData } from '../../../store/timeline/action-creators'

const EmployeeCardPage = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [formData, setFormData] = useState<EmployeeFormData | null>(null)
  const [enums, setEnums] = useState<Record<string, any[]>>({})
  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  const pageTitle = useMemo(
    () => (formData?.employee ? `Сотрудник: ${formData.employee.fio}` : `Сотрудник #${id}`),
    [id, formData],
  )

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/hr-employees',
        title: 'Сотрудники',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const entityType = 'hr_employee'

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  const loadData = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetchAPI(`/api/hr/employees/${id}`)

      if (response) {
        const processedData = getEmployeeFormData(response)

        if (!processedData) {
          setError('Не удалось обработать данные карточки сотрудника')
          return
        }

        setFormData(processedData)

        if (response.enums) {
          setEnums(response.enums)
        }
      }
    } catch (err: any) {
      console.error('Ошибка при загрузке данных сотрудника:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRefresh = useCallback(() => {
    loadData()
    setIsEditMode(false)
    // Обновляем ленту (события увольнения, переприёма, перевода и т.д.)
    if (id && id !== 'new') {
      dispatch(setTimelineClear())
      dispatch(getTimeLineData('hr_employee', id))
    }
  }, [loadData, id, dispatch])

  const handleEditModeChange = useCallback((editMode: boolean) => {
    setIsEditMode(editMode)
  }, [])

  const saveFormRef = useRef<(() => Promise<void>) | null>(null)

  const handleSave = useCallback(async () => {
    if (saveFormRef.current) {
      try {
        await saveFormRef.current()
        handleRefresh()
      } catch (error) {
        console.error('Ошибка при сохранении:', error)
      }
    }
  }, [handleRefresh])

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {loading && <Skeleton active />}
      {!loading && error && (
        <Result
          status={
            typeof error === 'object'
              ? (((error as IHttpError)?.statusCode || 'error') as ResultStatusType)
              : 'error'
          }
          title="Ошибка"
          subTitle={typeof error === 'object' ? (error as IHttpError)?.message : String(error)}
        />
      )}
      {!loading && !error && formData && (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <EmployeeFormHeader
            id={id}
            employee={formData.employee || null}
            enums={enums}
            isEditMode={isEditMode}
            onRefresh={handleRefresh}
            onEditModeChange={handleEditModeChange}
            onSave={handleSave}
          />
          <EmployeeCardContent
            id={id}
            formData={formData}
            loading={loading}
            isEditMode={isEditMode}
            onSaveRef={saveFormRef}
          />
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
}

export default EmployeeCardPage
